/**
 * The set of v2 scalar metric keys the backend exposes. camelCase end-to-end —
 * these strings appear in querystrings AND as response object keys.
 */
export type ScalarMetricKey =
  | 'aiMessages'
  | 'aiAcceptanceRate'
  | 'aiCoverage'
  | 'aiTicketCoverage'
  | 'aiAutomation'
  | 'aiAccuracy'
  | 'aiEdits'
  | 'avgAiResponsesPerTicket'
  | 'aiSurfacedAccuracy'
  | 'aiGeneratedCoverage'
  | 'aiSurfacedCoverage';

/**
 * The metrics shown on the Message Metrics tab's Overview section. Order
 * matters (grid layout). `avgAiResponsesPerTicket` is a member of the
 * `ScalarMetricKey` union but lives on the Ticket Metrics tab; it is fetched
 * by `TicketMetricsSection`, not by `OverviewSection`.
 */
export const SCALAR_METRIC_KEYS: ScalarMetricKey[] = ['aiMessages', 'aiAcceptanceRate', 'aiCoverage', 'aiAutomation', 'aiAccuracy', 'aiEdits'];

/**
 * Scalar keys fetched on the Ticket Metrics tab. Three real metrics (the
 * `Productivity Gain` card is a placeholder, no backend call yet).
 *   - aiTicketCoverage powers "Tickets Handled by AI" (distinct conversations
 *     handled by AI / distinct CS conversations, rendered as count/total)
 *   - avgAiResponsesPerTicket powers "Avg AI responses /ticket"
 *   - aiAutomation powers "Automation Rate" (rendered as percent)
 */
export const TICKET_METRIC_KEYS: ScalarMetricKey[] = ['aiTicketCoverage', 'avgAiResponsesPerTicket', 'aiAutomation'];

/**
 * One snapshot — count, total, and the derived percent.
 */
export interface MetricSnapshot {
  count: number;
  total: number;
  percent: number | null;
}

/**
 * Two snapshots side-by-side plus both trend forms:
 *  - `countChangePct` — relative growth of the count vs prev period (300 → 360 = +20%)
 *  - `percentPointChange` — literal subtraction of two percents (30% → 36% = +6 pp)
 *
 * The card decides which trend to surface based on what it's displaying.
 */
export interface ScalarMetricResult {
  current: MetricSnapshot;
  previous: MetricSnapshot;
  trend: {
    countChangePct: number | null;
    percentPointChange: number | null;
  };
}

export type ScalarMetricsBatch = Partial<Record<ScalarMetricKey, ScalarMetricResult>>;

export interface AnalyticsV2QueryParams {
  fromDate: string; // YYYY-MM-DD
  toDate: string;   // YYYY-MM-DD
  /** Account to read. Always explicit — the Control Panel has no own account. */
  accountId: number;
  /**
   * Brand to scope to, or null/undefined for the whole account.
   *
   * A STRING because `business_units.id` is a Postgres BIGINT that machine serialises as
   * a string, and because it goes straight onto the query string. Never coerced to a
   * number on the way out: kim-analytics validates it with a digit pattern.
   *
   * Unlike the product frontend there is no operator brand scope to follow here — the
   * Control Panel inspects OTHER accounts, so the brand belongs to the account being
   * viewed and is picked per view.
   */
  buId?: string | null;
}

/**
 * Series metric keys mirror scalar keys — a card and its trend chart share one
 * identifier end-to-end. Only metrics with a backend series implementation
 * appear here; cards without series simply don't render a chart.
 */
export type SeriesMetricKey = 'aiMessages' | 'aiAcceptanceRate' | 'aiCoverage' | 'aiAutomation' | 'aiAccuracy' | 'aiEdits';

export const SERIES_METRIC_KEYS: SeriesMetricKey[] = ['aiMessages', 'aiAcceptanceRate', 'aiCoverage', 'aiAutomation', 'aiAccuracy', 'aiEdits'];

export type Granularity = 'day' | 'week' | 'month';

export interface SeriesBucket {
  date: string;        // YYYY-MM-DD — start of bucket
  count: number;
  total: number;
  percent: number | null;
}

export interface SeriesMetricResult {
  granularity: Granularity;
  buckets: SeriesBucket[];
}

export type SeriesMetricsBatch = Partial<Record<SeriesMetricKey, SeriesMetricResult>>;

export interface AnalyticsV2SeriesQueryParams extends AnalyticsV2QueryParams {
  granularity: Granularity;
}


// ─── Categories ───────────────────────────────────────────────────────────────
// One row per category in the rollup for the Ticket Metrics tab's
// "Metrics by Category" table. Shape mirrors the backend response from
// `GET /api/analytics-v2/categories`.

export type TicketCategoryBucket = 'CS' | 'Non CS' | 'B2B' | 'Spam';

export interface CategoryTableRow {
  category: string;
  tickets: number;
  avgAiResponsesPerTicket: number | null;
  withAi: { count: number; percent: number | null };
  automated: { count: number; percent: number | null };
  ticketCategory: TicketCategoryBucket | null;
}

export interface AnalyticsV2CategoriesQueryParams extends AnalyticsV2QueryParams {
  /** Optional bucket filter. When unset, all categories return. */
  ticketCategory?: TicketCategoryBucket;
}


// ─── Coverage Funnel ────────────────────────────────────────────────────────────
// The Coverage Funnel section on the super-admin Internal Analytics page.
// Mirrors the shadow report's Coverage Funnel sheet: seven stages descending
// from CS Tickets, each with its share of csTickets. Shape mirrors the backend
// response from `GET /api/analytics/funnel`.

/** One funnel stage: raw count and its share of csTickets (already *100, 2dp). */
export interface CoverageFunnelStage {
  count: number;
  pct: number | null;
}

export interface CoverageFunnelResult {
  csTickets: number;
  aiGenerated: CoverageFunnelStage; // AI Generated Response
  surfaced: CoverageFunnelStage; // Surfaced (confidence >= 0.5)
  nonSurfaced: CoverageFunnelStage; // Non-Surfaced
  hitlPending: CoverageFunnelStage; // HITL Pending
  agentAdded: CoverageFunnelStage; // Agent Added to Composer
  agentSent: CoverageFunnelStage; // Agent Sent to Customer
  hitl: { approved: number; rejected: number; pending: number };
}
